// @ts-check
// Module core/render-biblio
// renders the biblio data pre-processed in core/biblio

import { addId, getIntlData, showError, toId, xmlEscape } from "./utils.js";
import { biblio } from "./biblio.js";
import { html } from "./import-maps.js";

export const name = "core/render-biblio";

/** @param {string} ref */
function bibRefId(ref) {
  return `bib-${toId(ref)}`;
}

const localizationStrings = {
  en: {
    info_references: "Informative references",
    norm_references: "Normative references",
    references: "References",
    reference_not_found: "Reference not found.",
  },
  ko: {
    references: "참조",
  },
  nl: {
    info_references: "Informatieve referenties",
    norm_references: "Normatieve referenties",
    references: "Referenties",
  },
  es: {
    info_references: "Referencias informativas",
    norm_references: "Referencias normativas",
    references: "Referencias",
    reference_not_found: "Referencia no encontrada.",
  },
  fr: {
    info_references: "Références informatives",
    norm_references: "Références normatives",
    references: "Références",
    reference_not_found: "Référence non trouvée.",
  },
  ja: {
    info_references: "参照用参考文献",
    norm_references: "規範的参考文献",
    references: "参考文献",
  },
  de: {
    info_references: "Weiterführende Informationen",
    norm_references: "Normen und Spezifikationen",
    references: "Referenzen",
  },
  zh: {
    info_references: "非规范性引用",
    norm_references: "规范性引用",
    references: "参考文献",
  },
  cs: {
    info_references: "Informativní odkazy",
    norm_references: "Normativní odkazy",
    references: "Odkazy",
    reference_not_found: "Odkaz nebyl nalezen.",
  },
};

const l10n = getIntlData(localizationStrings);

const REF_STATUSES = new Map([
  ["CR", "W3C Candidate Recommendation"],
  ["ED", "W3C Editor's Draft"],
  ["LCWD", "W3C Last Call Working Draft"],
  ["NOTE", "W3C Working Group Note"],
  ["PR", "W3C Proposed Recommendation"],
  ["REC", "W3C Recommendation"],
  ["WD", "W3C Working Draft"],
]);

/** @param {Conf} conf */
export function run(conf) {
  const informs = Array.from(conf.informativeReferences);
  const norms = Array.from(conf.normativeReferences);

  if (!informs.length && !norms.length) return;

  /** @type {HTMLElement} */
  const refSection =
    document.querySelector("section#references") ||
    html`<section id="references"></section>`;

  if (!document.querySelector("section#references > :is(h2, h1)")) {
    // We use a h1 here because this could be structured from markdown
    // which would otherwise end up in the wrong document order
    // when the document is restructured.
    refSection.prepend(html`<h1>${l10n.references}</h1>`);
  }

  refSection.classList.add("appendix");

  if (norms.length) {
    const sec = createReferencesSection(norms, l10n.norm_references);
    refSection.appendChild(sec);
  }
  if (informs.length) {
    const sec = createReferencesSection(informs, l10n.info_references);
    refSection.appendChild(sec);
  }

  document.body.appendChild(refSection);
}

/**
 * @param {string[]} refs
 * @param {string} title
 * @returns {HTMLElement}
 */
function createReferencesSection(refs, title) {
  const { goodRefs, badRefs } = groupRefs(refs.map(toRefContent));
  const uniqueRefs = getUniqueRefs(goodRefs);

  const refsToShow = uniqueRefs
    .concat(badRefs)
    .sort((a, b) =>
      a.ref.toLocaleLowerCase().localeCompare(b.ref.toLocaleLowerCase())
    );

  const sec = html`<section>
    <h3>${title}</h3>
    <dl class="bibliography">${refsToShow.map(showRef)}</dl>
  </section>`;
  addId(sec, "", title);

  const aliases = getAliases(goodRefs);
  decorateInlineReference(uniqueRefs, aliases);
  warnBadRefs(badRefs);

  return sec;
}

/**
 * returns refcontent and unique key for a reference among its aliases
 * and warns about circular references
 * @param {String} ref
 * @typedef {ReturnType<typeof toRefContent>} Ref
 */
function toRefContent(ref) {
  let refcontent = biblio[ref];
  let key = ref;
  const circular = new Set([key]);
  while (refcontent && refcontent.aliasOf) {
    if (circular.has(refcontent.aliasOf)) {
      // @ts-expect-error circular
      refcontent = null;
      const msg = `Circular reference in biblio DB between [\`${ref}\`] and [\`${key}\`].`;
      showError(msg, name);
    } else {
      key = refcontent.aliasOf;
      refcontent = biblio[key];
      circular.add(key);
    }
  }
  if (refcontent && !refcontent.id) {
    refcontent.id = ref.toLowerCase();
  }
  return { ref, refcontent };
}

/** @param {Ref[]} refs */
function groupRefs(refs) {
  const goodRefs = [];
  const badRefs = [];
  for (const ref of refs) {
    if (ref.refcontent) {
      goodRefs.push(ref);
    } else {
      badRefs.push(ref);
    }
  }
  return { goodRefs, badRefs };
}

/** @param {Ref[]} refs */
function getUniqueRefs(refs) {
  /** @type {Map<string, Ref>} */
  const uniqueRefs = new Map();
  for (const ref of refs) {
    if (!uniqueRefs.has(ref.refcontent?.id ?? "")) {
      // the condition ensures that only the first used [[TERM]]
      // shows up in #references section
      uniqueRefs.set(ref.refcontent?.id ?? "", ref);
    }
  }
  return [...uniqueRefs.values()];
}

/**
 * Render an inline citation
 *
 * @param {String} ref the inline reference.
 * @param {String} [linkText] custom link text
 * @returns HTMLElement
 */
export function renderInlineCitation(ref, linkText) {
  const key = ref.replace(/^(!|\?)/, "");
  const href = `#${bibRefId(key)}`;
  const text = linkText || key;
  const elem = html`<cite
    ><a class="bibref" href="${href}" data-link-type="biblio">${text}</a></cite
  >`;
  return linkText ? elem : html`[${elem}]`;
}

/**
 * renders a reference
 * @param {Ref} reference
 */
function showRef(reference) {
  const { ref, refcontent } = reference;
  const refId = bibRefId(ref);
  const result = html`
    <dt id="${refId}">[${ref}]</dt>
    <dd>
      ${
        refcontent
          ? { html: stringifyReference(refcontent) }
          : html`<em class="respec-offending-element"
              >${l10n.reference_not_found}</em
            >`
      }
    </dd>
  `;
  return result;
}

/** @param {BiblioData|string} ref */
function stringifyReference(ref) {
  if (typeof ref === "string") return ref;
  let output = `<cite>${ref.title}</cite>`;

  output = ref.href ? `<a href="${ref.href}">${output}</a>. ` : `${output}. `;

  if (ref.authors) {
    if (!Array.isArray(ref.authors)) {
      const msg = `The "authors" field in reference "${ref.id || ref.title}" must be an array.`;
      const hint = `Use \`authors: [${JSON.stringify(ref.authors)}]\` instead of \`authors: ${JSON.stringify(ref.authors)}\`.`;
      showError(msg, name, { hint });
      ref.authors = [ref.authors];
    }
    if (ref.authors.length) {
      output += ref.authors.join("; ");
      if (ref.etAl) output += " et al";
      if (!output.endsWith(".")) output += ". ";
    }
  }
  if (ref.publisher) {
    // When pages follow, use a comma after publisher instead of a period.
    const publisherEnd = ref.pages ? "," : ".";
    const publisherText = ref.publisher.trim();
    const normalizedPublisher = publisherText.endsWith(".")
      ? publisherText.slice(0, -1)
      : publisherText;
    output = `${output} ${normalizedPublisher}${publisherEnd} `;
  }
  if (ref.pages) output += `pp. ${xmlEscape(ref.pages)}. `;
  if (ref.date) output += `${ref.date}. `;
  if (ref.status) output += `${REF_STATUSES.get(ref.status) || ref.status}. `;
  if (ref.href) output += `URL: <a href="${ref.href}">${ref.href}</a>`;
  return output;
}

/**
 * get aliases for a reference "key"
 * @param {Ref[]} refs
 */
function getAliases(refs) {
  /** @type {Map<string, string[]>} */
  const aliases = new Map();
  for (const ref of refs) {
    const key = ref.refcontent?.id ?? "";
    const keys = !aliases.has(key)
      ? aliases.set(key, []).get(key)
      : aliases.get(key);
    keys?.push(ref.ref);
  }
  return aliases;
}

/**
 * fix biblio reference URLs
 * Add title attribute to references
 * @param {Ref[]} refs
 * @param {Map<string, string[]>} aliases
 */
function decorateInlineReference(refs, aliases) {
  refs
    .map(({ ref, refcontent }) => {
      const refUrl = `#${bibRefId(ref)}`;
      const selectors = (aliases.get(refcontent.id ?? "") ?? [])
        .map(alias => `a.bibref[href="#${bibRefId(alias)}"]`)
        .join(",");
      /** @type {NodeListOf<HTMLAnchorElement>} */
      const elems = document.querySelectorAll(selectors);
      return { refUrl, elems, refcontent };
    })
    .forEach(({ refUrl, elems, refcontent }) => {
      elems.forEach(a => {
        a.setAttribute("href", refUrl);
        a.setAttribute("title", refcontent.title);
        a.dataset.linkType = "biblio";
      });
    });
}

/**
 * warn about bad references
 * @param {Ref[]} refs
 */
function warnBadRefs(refs) {
  for (const { ref } of refs) {
    /** @type {NodeListOf<HTMLElement>} */
    const links = document.querySelectorAll(
      `a.bibref[href="#${bibRefId(ref)}"]`
    );
    const elements = [...links].filter(
      ({ textContent: t }) => t.toLowerCase() === ref.toLowerCase()
    );
    const msg = `Reference "[${ref}]" not found.`;
    const hint = `Search for ["${ref}"](https://www.specref.org?q=${ref}) on Specref to see if it exists or if it's misspelled.`;
    showError(msg, name, { hint, elements });
  }
}
